/**
 * Pure selection-state logic for the modular pricing calculator — the
 * reducer, the computeBill input mapping, and URL (de)serialization so a
 * prospect's configuration survives sharing links and the signup redirect.
 * Kept free of React so the math and invariants are unit-testable.
 */

import {
  ACCOUNT_KINDS,
  MODULE_KEYS,
  PLAN_TIERS,
  computeBill,
  isModuleKey,
  moduleComingSoonLabel,
  addonComingSoonLabel,
  sanitizeComingSoonSelection,
  type AccountKind,
  type BillBreakdown,
  type BillInput,
  type ModuleKey,
  type PlanTier,
} from "@shared/modular-pricing";

export interface PlanSelection {
  accountKind: AccountKind;
  planTier: PlanTier;
  /** Persisted even on Pro (where every module is included) so a later
   * downgrade to Essentials restores the same à-la-carte picks. */
  modules: ModuleKey[];
  roomsOnPadsplit: number;
  roomsOffPadsplit: number;
  revenueAddon: boolean;
  researchAddon: boolean;
  /** PM only — owners with portal access. */
  ownersCount: number;
  /** Prospect-only exploration control; real accounts use per-owner data. */
  waivingOwnerPercent: number;
  cpmListing: boolean;
  offMarketplaceVendors: number;
}

export const MAX_ROOMS_INPUT = 5000;
export const MAX_OWNERS_INPUT = 500;
export const MAX_VENDORS_INPUT = 200;

export function defaultSelection(accountKind: AccountKind = "operator"): PlanSelection {
  return {
    accountKind,
    planTier: "essentials",
    modules: [],
    roomsOnPadsplit: 10,
    roomsOffPadsplit: 0,
    revenueAddon: false,
    researchAddon: false,
    ownersCount: 0,
    waivingOwnerPercent: 0,
    cpmListing: false,
    offMarketplaceVendors: 0,
  };
}

export type PlanSelectionAction =
  | { type: "setAccountKind"; accountKind: AccountKind }
  | { type: "setPlanTier"; planTier: PlanTier }
  | { type: "toggleModule"; module: ModuleKey }
  | { type: "setRoomsOn"; value: number }
  | { type: "setRoomsOff"; value: number }
  | { type: "setOwners"; value: number }
  | { type: "setWaivingPercent"; value: number }
  | { type: "setVendors"; value: number }
  | { type: "setRevenueAddon"; value: boolean }
  | { type: "setResearchAddon"; value: boolean }
  | { type: "setCpmListing"; value: boolean }
  | { type: "hydrate"; selection: Partial<PlanSelection> };

function clamp(value: number, max: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(max, Math.max(0, Math.floor(value)));
}

/** Strip anything the account kind can't hold — the ONE place kind rules live client-side. */
function enforceKindRules(sel: PlanSelection): PlanSelection {
  if (sel.accountKind === "operator") {
    return { ...sel, ownersCount: 0, waivingOwnerPercent: 0, cpmListing: false };
  }
  return { ...sel, revenueAddon: false, researchAddon: false };
}

export function planSelectionReducer(state: PlanSelection, action: PlanSelectionAction): PlanSelection {
  switch (action.type) {
    case "setAccountKind":
      return enforceKindRules({ ...state, accountKind: action.accountKind });
    case "setPlanTier":
      return { ...state, planTier: action.planTier };
    case "toggleModule": {
      // Coming-soon modules render on the calculator but are not selectable.
      if (moduleComingSoonLabel(action.module) != null) return state;
      const has = state.modules.includes(action.module);
      return {
        ...state,
        modules: has ? state.modules.filter((m) => m !== action.module) : [...state.modules, action.module],
      };
    }
    case "setRoomsOn":
      return { ...state, roomsOnPadsplit: clamp(action.value, MAX_ROOMS_INPUT) };
    case "setRoomsOff":
      return { ...state, roomsOffPadsplit: clamp(action.value, MAX_ROOMS_INPUT) };
    case "setOwners":
      return { ...state, ownersCount: clamp(action.value, MAX_OWNERS_INPUT) };
    case "setWaivingPercent":
      return { ...state, waivingOwnerPercent: clamp(action.value, 100) };
    case "setVendors":
      return { ...state, offMarketplaceVendors: clamp(action.value, MAX_VENDORS_INPUT) };
    case "setRevenueAddon":
      if (action.value && addonComingSoonLabel("revenue") != null) return state;
      return enforceKindRules({ ...state, revenueAddon: action.value });
    case "setResearchAddon":
      return enforceKindRules({ ...state, researchAddon: action.value });
    case "setCpmListing":
      return enforceKindRules({ ...state, cpmListing: action.value });
    case "hydrate": {
      // Partial payloads often carry explicit-undefined keys (e.g. built from
      // an API object) — they must not clobber the defaults.
      const cleaned = Object.fromEntries(
        Object.entries(action.selection).filter(([, v]) => v !== undefined),
      );
      return enforceKindRules({ ...defaultSelection(), ...state, ...cleaned });
    }
    default:
      return state;
  }
}

/**
 * Map a selection onto computeBill's input. In prospect mode the waived-owner
 * count is derived from the exploration percentage; account mode passes REAL
 * per-owner counts instead (never a percentage).
 */
export function billInputForSelection(
  sel: PlanSelection,
  realOwnerData?: { portalOwners: number; waivedOwners: number },
): BillInput {
  const pm = sel.accountKind === "property_manager";
  const ownerPortalCount = pm ? (realOwnerData ? realOwnerData.portalOwners : sel.ownersCount) : undefined;
  const waivedOwnerCount = pm
    ? realOwnerData
      ? realOwnerData.waivedOwners
      : Math.round((sel.ownersCount * sel.waivingOwnerPercent) / 100)
    : undefined;
  // Sanitized so stale state (old URL, pre-launch toggle) can never make the
  // live calculator throw COMING_SOON — new selections are already blocked at
  // the reducer.
  return sanitizeComingSoonSelection({
    accountKind: sel.accountKind,
    planTier: sel.planTier,
    modules: sel.planTier === "essentials" ? sel.modules : [],
    roomsOnPadsplit: sel.roomsOnPadsplit,
    roomsOffPadsplit: sel.roomsOffPadsplit,
    revenueAddon: sel.accountKind === "operator" ? sel.revenueAddon : undefined,
    researchAddon: sel.accountKind === "operator" ? sel.researchAddon : undefined,
    ownerPortalCount,
    waivedOwnerCount,
    cpmListing: pm ? sel.cpmListing : undefined,
    offMarketplaceVendorCount: sel.offMarketplaceVendors,
  });
}

export function quoteForSelection(
  sel: PlanSelection,
  realOwnerData?: { portalOwners: number; waivedOwners: number },
): BillBreakdown {
  return computeBill(billInputForSelection(sel, realOwnerData));
}

// ─── URL state (shareable prospect quotes) ────────────────────────────────────

export function serializeSelection(sel: PlanSelection): string {
  const params = new URLSearchParams();
  params.set("type", sel.accountKind === "property_manager" ? "pm" : "op");
  params.set("plan", sel.planTier);
  params.set("on", String(sel.roomsOnPadsplit));
  params.set("off", String(sel.roomsOffPadsplit));
  if (sel.modules.length > 0) params.set("mods", sel.modules.join(","));
  if (sel.revenueAddon) params.set("revenue", "1");
  if (sel.researchAddon) params.set("research", "1");
  if (sel.ownersCount > 0) params.set("owners", String(sel.ownersCount));
  if (sel.waivingOwnerPercent > 0) params.set("waive", String(sel.waivingOwnerPercent));
  if (sel.cpmListing) params.set("cpm", "1");
  if (sel.offMarketplaceVendors > 0) params.set("vendors", String(sel.offMarketplaceVendors));
  return params.toString();
}

/** Garbage-tolerant: unknown values fall back to defaults, never throw. */
export function parseSelection(search: string): PlanSelection {
  const params = new URLSearchParams(search);
  const base = defaultSelection();
  const type = params.get("type");
  const accountKind: AccountKind =
    type === "pm" ? "property_manager" : type === "op" ? "operator" : base.accountKind;
  const rawPlan = params.get("plan") ?? "";
  const planTier: PlanTier = (PLAN_TIERS as readonly string[]).includes(rawPlan)
    ? (rawPlan as PlanTier)
    : base.planTier;
  const num = (key: string, fallback: number, max: number) => {
    const raw = Number(params.get(key));
    return Number.isFinite(raw) && params.get(key) !== null ? clamp(raw, max) : fallback;
  };
  const modules = (params.get("mods") ?? "")
    .split(",")
    .map((m) => m.trim())
    .filter(isModuleKey)
    .filter((m) => moduleComingSoonLabel(m) == null);
  return enforceKindRules({
    accountKind,
    planTier,
    modules,
    roomsOnPadsplit: num("on", base.roomsOnPadsplit, MAX_ROOMS_INPUT),
    roomsOffPadsplit: num("off", base.roomsOffPadsplit, MAX_ROOMS_INPUT),
    revenueAddon: params.get("revenue") === "1" && addonComingSoonLabel("revenue") == null,
    researchAddon: params.get("research") === "1",
    ownersCount: num("owners", 0, MAX_OWNERS_INPUT),
    waivingOwnerPercent: num("waive", 0, 100),
    cpmListing: params.get("cpm") === "1",
    offMarketplaceVendors: num("vendors", 0, MAX_VENDORS_INPUT),
  });
}

export function formatUsd(cents: number): string {
  const dollars = cents / 100;
  return dollars.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: dollars % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  });
}

// Re-exported so calculator components import selection logic + kinds from one place.
export { ACCOUNT_KINDS, MODULE_KEYS };
export type { AccountKind, BillBreakdown, ModuleKey, PlanTier };
